import { ObjectIdMap, ObjectId } from './view';
import { RawEvent } from '@fullstory/recording-api';
import { FSWebviewNotRecordedReason } from '@fullstory/mobile-flatbuffers';

export type WebViewId = [string, number];

interface WithBundle {
  bundle(this: any): string;
}

// There's a chance that the webview pops up on screen before it has really loaded
const WEBVIEW_LOAD_SLACK = 5000;

export class WebBundle {
  public recordingAllowed: boolean;
  public reasonNotRecorded: FSWebviewNotRecordedReason | null;
  public url: string;
  public epoch: number;
  public events: RawEvent[];
  public _start: number;
  public _end: number;

  isAlive(at: number) {
    return this._start <= at && at <= this._end;
  }
}

export class WebViewsState {
  public views: Readonly<ObjectIdMap<WebBundle>>;

  constructor(views: ObjectIdMap<WebBundle>) {
    this.views = views;
  }

  // Does this webview exist now, and does its epoch match up with what we expect?
  existsNow(id: ObjectId, epoch: number) {
    const v = this.views[id];
    if (v && v.epoch == epoch) {
      return true;
    }
    return false;
  }
}

export class WebBundles {
  private _webViews: Readonly<ObjectIdMap<WebBundle[]>>;

  constructor(
    improviseTimestamps: boolean,
    webViews: ObjectIdMap<{ [id: number]: WebBundleBuilder }>,
  ) {
    const sortedViews: ObjectIdMap<WebBundle[]> = {};
    for (const id in webViews) {
      const arr: WebBundle[] = (sortedViews[id] = []);
      for (const epoch in webViews[id]) {
        const b = webViews[id][epoch].freeze(improviseTimestamps, +epoch);
        if (b) {
          arr.push(b);
        } else {
          console.log(`WebView with id=${id} and epoch=${epoch} was invalid`);
        }
      }
      // Order by epoch start, earliest to latest
      arr.sort((a, b) => a._start - b._start);
    }

    this._webViews = sortedViews;
  }

  computeState(at: number): WebViewsState {
    const state: ObjectIdMap<WebBundle> = {};
    for (const id in this._webViews) {
      const arr = this._webViews[id];
      arr.forEach(w => {
        // We want the latest-starting set of bundles for this webview at this time
        if (w.isAlive(at)) {
          state[id] = w;
        }
      });
      if (!state[id] && arr.length >= 1) {
        // In the case where this is the first load of a given webview, we give it a few seconds of slack to wake up
        if (arr[0]._start - WEBVIEW_LOAD_SLACK <= at && arr[0]._end > at) {
          state[id] = arr[0];
        }
      }
    }
    return new WebViewsState(state);
  }

  getWebViews() {
    return this._webViews;
  }
}

export class WebBundlesBuilder {
  private _webViews: ObjectIdMap<{ [id: number]: WebBundleBuilder }> = {};
  private _improviseTimestamps = false;

  freeze(): WebBundles {
    return new WebBundles(this._improviseTimestamps, this._webViews);
  }

  findOrCreate(id: ObjectId, epoch: number): WebBundleBuilder {
    let map = this._webViews[id];
    if (!map) {
      map = this._webViews[id] = {};
    }

    let bundle = map[epoch];
    if (!bundle) {
      bundle = map[epoch] = new WebBundleBuilder();
    }

    return bundle;
  }

  improviseTimestamps() {
    if (!this._improviseTimestamps) {
      console.log(
        "Sorry, shared library data was bad at recording time, so we're going to have to improvise.",
      );
      this._improviseTimestamps = true;
    }
  }
}

export class WebBundleBuilder {
  private _webBundles: WithBundle[] = [];
  private _url: string;
  private _recordingAllowed: boolean = true;
  private _end: number;
  private _start: number;
  private _reasonNotRecorded: FSWebviewNotRecordedReason | null;

  addEvents(when: number, events: WithBundle) {
    this._webBundles.push(events);
  }

  setUrl(url: string, recordingAllowed: boolean) {
    this._url = url;
    this._recordingAllowed = recordingAllowed;
  }

  setNotRecordedReason(reason: FSWebviewNotRecordedReason) {
    this._reasonNotRecorded = reason;
  }

  setStart(start: number) {
    // Only allow decreasing values
    if (!this._start || start <= this._start) {
      this._start = start;
    }
  }

  setEnd(end: number) {
    if (!this._end || end >= this._end) {
      this._end = end;
    }
  }

  freeze(improviseTimestamps: boolean, epoch: number): WebBundle {
    // We want at least one bundle if we were allowed to record
    if (this._webBundles.length == 0 && this._recordingAllowed) {
      return null;
    }

    const bundle = new WebBundle();
    bundle.url = this._url;
    bundle.recordingAllowed = this._recordingAllowed;
    bundle.reasonNotRecorded = this._reasonNotRecorded;
    bundle._start = this._start;
    if (this._end) {
      bundle._end = this._end;
    } else {
      bundle._end = +Infinity;
    }
    bundle.epoch = epoch;
    bundle.events = [];
    // TODO(mmastrac): Could defer parsing of web events until we actually bring up the webview
    this._webBundles.forEach(b => {
      try {
        const arr = JSON.parse(b.bundle());
        Array.prototype.push.apply(bundle.events, arr);
      } catch (e) {
        console.error('Error parsing web bundle', e, b.bundle());
      }
    });
    if (improviseTimestamps) {
      bundle._start = +Infinity;
      bundle._end = -Infinity;
      bundle.events.forEach(b => {
        bundle._start = Math.min(bundle._start, b.When);
        bundle._end = Math.max(bundle._end, b.When);
      });
    }
    return bundle;
  }
}
